import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { BASE_ENDPOINT } from "../constants";
import auth from "../services/auth";
import AppService from "../services/app.service";

const isMissingAccountId = (value) =>
  value == null || value === "" || value === 0 || value === -1;

export function resolveAccountId() {
  const details = auth.getUserDetails() || {};
  const accountId = details.accId ?? details.accountId;
  return isMissingAccountId(accountId) ? null : accountId;
}

export async function ensureAccountId() {
  const existing = resolveAccountId();
  if (existing != null) return existing;

  const userId = auth.getUserId();
  if (!userId || userId === -1) return null;

  const profile = await AppService.get(userId, "users/profile");
  auth.setUserDetails(profile);
  return resolveAccountId();
}

/**
 * Live parent session from Redux. Hydrates profile when accType / name is missing.
 */
export function useParentSession() {
  const user = useSelector((state) => state.user);
  const details = user?.userDetail || {};

  const session = useMemo(() => {
    const firstName = details.firstName || "";
    const lastName = details.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim() || "User";
    const userId = user?.userId && user.userId !== -1 ? user.userId : null;
    const avatarFile = details.avatar;

    return {
      accType: details.accType || user?.accType || null,
      roleName: details.roleName || user?.role || null,
      fullName,
      firstName: firstName || fullName.split(" ")[0],
      avatarUrl: avatarFile ? `${BASE_ENDPOINT}avatar/${avatarFile}` : undefined,
      userId,
      accId: isMissingAccountId(details.accId) ? null : details.accId,
    };
  }, [user, details]);

  useEffect(() => {
    if (session.accType && session.roleName && session.fullName !== "User") {
      return undefined;
    }
    const userId = auth.getUserId();
    if (!userId || userId === -1) return undefined;

    let cancelled = false;
    AppService.get(userId, "users/profile")
      .then((profile) => {
        if (!cancelled) auth.setUserDetails(profile);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Hydrate once on mount. Re-running on every incomplete snapshot can loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return session;
}
